/**
 * Оценка уровня набора по итогу диагностики.
 *
 * Диагностика и раньше измеряла скорость, точность и слабые места — и
 * молчала о них: baseline записывался, тренировка по нему строилась, а
 * человек своего уровня не узнавал. Здесь измеренное превращается в ответ на
 * вопрос «какой у меня уровень и что мне делать».
 *
 * Оценка ничего не запирает. Она может ошибиться — например, если человек
 * искал клавиши глазами, — поэтому она не приговор, а точка отсчёта: осво­
 * енность каждой клавиши пересчитывается после каждого занятия, и первая
 * ошибка измерения исправляется сама (ТЗ FR-EN-61).
 */

import type { KeystrokeEvent, TypingMetrics } from '@type2x/shared-contracts';
import type { LearningConfig } from '@type2x/learning-engine';

import type { LevelVerdict, RecommendedStart, SkillLevel } from '../contracts/diagnostics.js';
import { SKILL_LEVELS, SKILL_LEVEL_FLOOR_CPM } from '../contracts/diagnostics.js';

/**
 * Точность, выше которой промахов практически нет.
 *
 * Ниже неё медленный набор объясняется незнанием клавиатуры: человек ещё не
 * помнит, где клавиша, и ошибается. Выше — движение верное, но небыстрое.
 */
const CLEAN_ENOUGH = 0.95;

/** Медиана значений; для пустого списка — ноль. */
function median(values: readonly number[]): number {
  if (values.length === 0) return 0;

  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);

  return sorted.length % 2 === 0
    ? ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2
    : (sorted[middle] ?? 0);
}

/** Ступень, которой соответствует скорость. */
export function levelForCpm(effectiveCpm: number): SkillLevel {
  let level: SkillLevel = SKILL_LEVELS[0];

  for (const candidate of SKILL_LEVELS) {
    if (effectiveCpm >= SKILL_LEVEL_FLOOR_CPM[candidate]) level = candidate;
  }

  return level;
}

/**
 * Оценивает уровень по метрикам прогона и промежуткам между нажатиями.
 *
 * Порог «медленно» — середина между уверенным нажатием и предельной
 * неуверенностью из конфигурации обучения. Своего числа здесь нет намеренно:
 * два разных представления о том, что такое «медленно», в соседних частях
 * приложения означали бы, что слово значит разное.
 */
export function judgeLevel(
  metrics: TypingMetrics,
  events: readonly KeystrokeEvent[],
  config: LearningConfig,
): LevelVerdict {
  // Первое нажатие промежутка не имеет: отсчитывать его не от чего.
  const intervals = events
    .map((event) => event.interKeyIntervalMs)
    .filter((interval) => interval > 0);

  const medianIntervalMs = median(intervals);
  // Точность набора, а не результата: скорость, полученная исправлениями,
  // скоростью не является (ТЗ §7.7).
  const effectiveCpm = metrics.cpm * metrics.firstTryAccuracy;
  const level = levelForCpm(effectiveCpm);

  const slowThresholdMs = (config.hesitationThresholdMs + config.hesitationCeilingMs) / 2;
  const accurateButSlow =
    metrics.firstTryAccuracy >= CLEAN_ENOUGH && medianIntervalMs >= slowThresholdMs;

  // С основ начинают двое: тот, кто ещё ищет клавиши, и тот, у кого движение
  // верное, но не стало привычным. Второму скорость не поможет, пока рука не
  // перестанет думать над каждым нажатием.
  //
  // На деле второе условие почти никогда не решает: медиана в 775 мс — это
  // около 77 знаков в минуту, а следующая ступень начинается со 100. Тот,
  // кого оно отмечает, и так на ступени «ищете клавиши».
  const recommendation: RecommendedStart =
    level === 'searching' || accurateButSlow ? 'fundamentals' : 'practice';

  return { level, effectiveCpm, medianIntervalMs, accurateButSlow, recommendation };
}
